import React, { useState, useRef } from 'react';
import axios from 'axios';
import { FiUpload, FiX } from 'react-icons/fi';

const API_BASE = process.env.REACT_APP_API_URL || '';

/** mediaType → URL folder (logo | shop | price) */
const MEDIA_TYPE_TO_FOLDER = {
    shopLogo: 'logo',
    shopImage: 'shop',
    shopPriceImage: 'price',
};

/** Proxy URL so frontend uses API path: /api/shops/:id/media/:folder/:filename */
function getShopMediaDisplayUrl(shopId, mediaType, fullUrl) {
    if (!shopId || !mediaType || !fullUrl) return fullUrl || '';
    const folder = MEDIA_TYPE_TO_FOLDER[mediaType] || mediaType;
    const filename = fullUrl.split('/').pop() || '';
    if (!filename) return fullUrl;
    return `${API_BASE}/api/shops/${shopId}/media/${folder}/${encodeURIComponent(filename)}`;
}

/**
 * Drag & drop + file picker for shop logo and shop images.
 * shopId required (edit mode only).
 */
const ShopMediaUploadSection = ({
    label,
    mediaType,
    urls = [],
    onUrlsChange,
    shopId,
    multiple = true,
    disabled = false,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    const folder = MEDIA_TYPE_TO_FOLDER[mediaType] || 'shop';

    const handleFiles = async (files) => {
        if (!files || files.length === 0 || !shopId) return;
        const list = Array.from(files);
        setError('');
        setUploading(true);
        try {
            const formData = new FormData();
            list.forEach((f) => formData.append('files', f));
            const token = localStorage.getItem('token');
            const res = await axios.post(
                `${API_BASE}/api/shops/${shopId}/media/${folder}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                    timeout: 60000,
                }
            );
            if (res.data.success && res.data.data && Array.isArray(res.data.data[mediaType])) {
                onUrlsChange && onUrlsChange(res.data.data[mediaType]);
            }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Upload failed';
            setError(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveUrl = async (url) => {
        if (!shopId) return;
        setError('');
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE}/api/shops/${shopId}/media`, {
                params: { mediaType, url },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true',
                },
            });
            onUrlsChange && onUrlsChange(urls.filter((u) => u !== url));
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Delete failed');
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        if (disabled || !shopId) return;
        handleFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => e.preventDefault();

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    disabled ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-300 bg-gray-50 cursor-pointer hover:border-blue-400'
                }`}
                onClick={() => !disabled && shopId && inputRef.current && inputRef.current.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple={multiple}
                    className="hidden"
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = '';
                    }}
                />
                <FiUpload className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-1 text-sm text-gray-600">Drag & drop or click to upload images</p>
            </div>
            {uploading && <p className="text-sm text-blue-600">Uploading…</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {urls.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {urls.map((url) => {
                        const displaySrc = shopId ? getShopMediaDisplayUrl(shopId, mediaType, url) : url;
                        return (
                            <div key={url} className="relative group">
                                <img
                                    src={displaySrc}
                                    alt=""
                                    className="w-24 h-24 object-cover rounded border border-gray-200"
                                />
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveUrl(url);
                                    }}
                                    className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-90 group-hover:opacity-100"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ShopMediaUploadSection;
